import type { OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import type { ComponentProps } from 'react'
import { useCallback, useRef } from 'react'
import * as React from 'react'
import OfferCalendarDatePicker from './OfferCalendarDatePicker'
import OfferCalendarDatePickerSelectBox from './OfferCalendarDatePickerSelectBox'
import OfferDateSelectChip from '../DatePicker/OfferDateSelectChip'
import DropdownSheetBase from 'LuxKit/Dropdown/Sheet/DropdownSheetBase'
import { useAssistantSurfaceDropdownBoundary } from 'tripPlanner/assistant/layout/AssistantSurfaceLayoutContext'

interface Props extends Pick<ComponentProps<typeof DropdownSheetBase>, 'autoHeight' | 'boundaryRef' | 'open' | 'placement' | 'portaled'> {
  useRooms?: boolean
  defaultDayPicker?: boolean
  defaultMonth?: string
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  /**
   * Provide a list of 'sub filters' to show as part of this component
   */
  subFilters?: Array<OfferPageFilterType>
  className?: string
  /**
   * How the dropdown's trigger is rendered: the labelled `box` (default), or the `chip`
   * the offer page's compact filter rows use.
   */
  triggerVariant?: 'box' | 'chip'
}

function OfferCalendarDatePickerSelect(props: Props) {
  const {
    boundaryRef,
    placement,
    open,
    useRooms,
    defaultDayPicker,
    defaultMonth,
    onClick,
    subFilters,
    className,
    autoHeight,
    portaled,
    triggerVariant = 'box',
  } = props
  const isMobile = useScreenSizeOnly('mobile')

  const dispatch = useOfferPageDispatch()

  const onClose = useCallback(() => {
    dispatch({ type: OfferPageActions.setRoomType, room: undefined })
    dispatch({ type: OfferPageActions.closeFilter })
  }, [dispatch])
  const scrollRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownBoundary = useAssistantSurfaceDropdownBoundary(boundaryRef)

  return (<div className={className}>
    {triggerVariant === 'chip' ? <OfferDateSelectChip ref={triggerRef} onClick={onClick} /> : (
      <OfferCalendarDatePickerSelectBox
        ref={triggerRef}
        onClick={onClick}
      />
    )}
    <DropdownSheetBase
      open={open && !isMobile}
      size="XL"
      height="max"
      placement={placement}
      onClose={onClose}
      anchorRef={triggerRef}
      triggerRef={triggerRef}
      autoHeight={dropdownBoundary.autoHeight ?? autoHeight}
      boundaryRef={dropdownBoundary.boundaryRef}
      data-testid="offer-checkin-select"
      keepOpenOnModalClick
      portaled={dropdownBoundary.portaled ?? portaled}
    >
      <OfferCalendarDatePicker
        defaultDayPicker={defaultDayPicker}
        defaultMonth={defaultMonth}
        useRooms={useRooms}
        subFilters={subFilters}
        scrollRef={scrollRef}
      />
    </DropdownSheetBase>
  </div>
  )
}

export default withOfferPageState(state => ({
  open: state.activeFilter === 'checkin',
}), OfferCalendarDatePickerSelect)
